import React, { useState, useEffect } from "react";
import { useLocationReportsOperations } from "../../hooks/useLocationReportsOperations";
import { useLayout } from "../../context/LayoutContext";
import LoadingAnimation from "../../components/loading/LoadingAnimation";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/es";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { TextField, Button, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BarChartIcon from "@mui/icons-material/BarChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "../../assets/scss/views/LocationReportView.scss";

// Configuración de dayjs para soporte de localización
dayjs.extend(localizedFormat);
dayjs.locale("es");

// Horas a mostrar en el reporte
const REPORT_HOURS = ["08:00", "10:00", "12:00", "14:00", "17:00"];

const LocationReportView = () => {
  const { theme } = useLayout();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [formattedDate, setFormattedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [expandedHours, setExpandedHours] = useState({});
  const [showStats, setShowStats] = useState(false);
  
  const {
    useLocationReportsByDate,
    useExportDailyReportsExcel,
    useMonthlyStats,
  } = useLocationReportsOperations();

  const { data, isLoading, isError, refetch } = useLocationReportsByDate(formattedDate);
  const { mutate: exportExcel, isLoading: isExporting } = useExportDailyReportsExcel();
  
  // Obtener el año y mes actual para estadísticas
  const currentYear = selectedDate.year();
  const currentMonth = selectedDate.month() + 1;
  
  const { data: statsData, isLoading: isLoadingStats } = useMonthlyStats(
    currentYear,
    currentMonth
  );

  // Actualizar la fecha formateada cuando cambia la fecha seleccionada
  useEffect(() => {
    if (selectedDate) {
      setFormattedDate(selectedDate.format("YYYY-MM-DD"));
    }
  }, [selectedDate]);

  // Manejar cambio de fecha
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Exportar a Excel
  const handleExportExcel = () => {
    exportExcel(formattedDate);
  };

  // Alternar vista de estadísticas
  const toggleStats = () => {
    setShowStats(!showStats);
  };

  // Expandir/colapsar horas de un vehículo
  const toggleVehicleHours = (plate) => {
    setExpandedHours(prev => ({
      ...prev,
      [plate]: !prev[plate]
    }));
  };

  // Formatear hora desde formato 24h (HH:MM:SS)
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    
    // Extraer solo las horas y minutos (HH:MM)
    const parts = timeString.split(":");
    return `${parts[0]}:${parts[1]}`;
  };

  // Procesar los datos de los vehículos
  const processVehicleData = () => {
    if (!data || !data.data) return [];
    
    return data.data.map(vehicle => {
      // Extraer la información básica del vehículo
      const vehicleInfo = {
        plate: vehicle.vehicle_plate,
        company: "COOTRANSPETROLS",
        provider: vehicle.provider,
        operator_name: vehicle.operator_name,
        operator_id: vehicle.operator_identification,
        operator_phone: vehicle.operator_phone,
        departure_time: formatTime(vehicle.departure_time),
        days: {}
      };

      // Procesar reportes por hora para los 3 días
      for (let day = 1; day <= 3; day++) {
        vehicleInfo.days[day] = {};
        
        // Poblar datos para cada hora de reporte
        REPORT_HOURS.forEach(hourStr => {
          const hour = parseInt(hourStr.split(":")[0]);
          const reportHour = vehicle.hourly_reports[hour];
          
          if (reportHour) {
            const timeField = `report_time_day${day}`;
            const speedField = `speed_day${day}`;
            const locationField = `location_day${day}`;
            const latField = `latitude_day${day}`;
            const longField = `longitude_day${day}`;
            
            vehicleInfo.days[day][hourStr] = {
              time: reportHour[timeField] ? formatTime(reportHour[timeField]) : "-",
              speed: reportHour[speedField] || 0,
              location: reportHour[locationField] || "Sin reporte",
              latitude: reportHour[latField],
              longitude: reportHour[longField]
            };
          } else {
            vehicleInfo.days[day][hourStr] = {
              time: "-", 
              speed: 0,
              location: "Sin reporte",
              latitude: null,
              longitude: null
            };
          }
        });
      }
      
      return vehicleInfo;
    });
  };

  const vehiclesData = processVehicleData();

  return (
    <div className={`GM__${theme}-location-report-container`}>
      <div className={`GM__${theme}-location-report-header`}>
        <Typography variant="h5" className={`GM__${theme}-location-report-title`}>
          Reporte de Ubicación de Vehículos
        </Typography>
        
        <div className={`GM__${theme}-location-report-controls`}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Seleccionar fecha"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{ textField: { size: 'small' } }}
              className={`GM__${theme}-location-report-datepicker`}
            />
          </LocalizationProvider>
          
          <div className={`GM__${theme}-location-report-actions`}>
            <Tooltip title="Actualizar datos">
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Exportar a Excel">
              <IconButton 
                onClick={handleExportExcel} 
                disabled={isLoading || isExporting || !vehiclesData.length}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={showStats ? "Ocultar estadísticas" : "Ver estadísticas"}>
              <IconButton onClick={toggleStats}>
                <BarChartIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      {showStats && (
        <Paper className={`GM__${theme}-location-report-stats`}>
          {isLoadingStats ? (
            <LoadingAnimation />
          ) : statsData?.data ? (
            <div className={`GM__${theme}-location-report-stats-content`}>
              <Typography variant="h6">
                Estadísticas del Mes: {selectedDate.format("MMMM YYYY")}
              </Typography>
              
              <div className={`GM__${theme}-location-report-stats-grid`}>
                <div className={`GM__${theme}-location-report-stat-item`}>
                  <Typography variant="subtitle2">Vehículos monitoreados</Typography>
                  <Typography variant="h4">{statsData.data.stats.total_vehicles || 0}</Typography>
                </div>
                
                <div className={`GM__${theme}-location-report-stat-item`}>
                  <Typography variant="subtitle2">Total de reportes</Typography>
                  <Typography variant="h4">{statsData.data.stats.total_reports || 0}</Typography>
                </div>
                
                <div className={`GM__${theme}-location-report-stat-item`}>
                  <Typography variant="subtitle2">Velocidad máxima</Typography>
                  <Typography variant="h4">{statsData.data.stats.max_speed || 0} km/h</Typography>
                </div>
                
                <div className={`GM__${theme}-location-report-stat-item`}>
                  <Typography variant="subtitle2">Velocidad promedio</Typography>
                  <Typography variant="h4">
                    {statsData.data.stats.avg_speed 
                      ? Math.round(statsData.data.stats.avg_speed) 
                      : 0} km/h
                  </Typography>
                </div>
                
                <div className={`GM__${theme}-location-report-stat-item`}>
                  <Typography variant="subtitle2">Días con reportes</Typography>
                  <Typography variant="h4">{statsData.data.stats.days_with_reports || 0}</Typography>
                </div>
              </div>
            </div>
          ) : (
            <Typography>No hay estadísticas disponibles</Typography>
          )}
        </Paper>
      )}

      <div className={`GM__${theme}-location-report-content`}>
        {isLoading ? (
          <div className={`GM__${theme}-location-report-loading`}>
            <LoadingAnimation />
            <Typography>Cargando reportes...</Typography>
          </div>
        ) : isError ? (
          <div className={`GM__${theme}-location-report-error`}>
            <Typography>Error al cargar los reportes. Intente nuevamente.</Typography>
            <Button 
              variant="contained" 
              onClick={() => refetch()}
              className={`GM__${theme}-location-report-retry-btn`}
            >
              Reintentar
            </Button>
          </div>
        ) : vehiclesData.length === 0 ? (
          <div className={`GM__${theme}-location-report-empty`}>
            <Typography>No hay reportes disponibles para esta fecha.</Typography>
          </div>
        ) : (
          <div className={`GM__${theme}-location-report-table-container`}>
            <table className={`GM__${theme}-location-report-table`}>
              <thead>
                <tr className="main-header-row">
                  <th rowSpan="2">N°</th>
                  <th rowSpan="2">Placa</th>
                  <th rowSpan="2">Empresa</th>
                  <th rowSpan="2">Fecha Cargue</th>
                  <th rowSpan="2">Hora Salida</th>
                  <th rowSpan="2">Conductor</th>
                  <th rowSpan="2">Cédula</th>
                  <th rowSpan="2">Teléfono</th>
                  <th colSpan="3">Día 1</th>
                  <th colSpan="3">Día 2</th>
                  <th colSpan="3">Día 3</th>
                  <th rowSpan="2">Acciones</th>
                </tr>
                <tr className="sub-header-row">
                  {/* Día 1 */}
                  <th>Hora Reporte</th>
                  <th>Vel. Máxima</th>
                  <th>Ubicación</th>
                  {/* Día 2 */}
                  <th>Hora Reporte</th>
                  <th>Vel. Máxima</th>
                  <th>Ubicación</th>
                  {/* Día 3 */}
                  <th>Hora Reporte</th>
                  <th>Vel. Máxima</th>
                  <th>Ubicación</th>
                </tr>
              </thead>
              <tbody>
                {vehiclesData.map((vehicle, index) => (
                  <React.Fragment key={vehicle.plate}>
                    {/* Fila principal con resumen */}
                    <tr className={`GM__${theme}-location-report-vehicle-row`}>
                      <td>{index + 1}</td>
                      <td>{vehicle.plate}</td>
                      <td>{vehicle.company}</td>
                      <td>{selectedDate.format("DD/MM/YYYY")}</td>
                      <td>{vehicle.departure_time}</td>
                      <td>{vehicle.operator_name || "-"}</td>
                      <td>{vehicle.operator_id || "-"}</td>
                      <td>{vehicle.operator_phone || "-"}</td>
                      
                      {/* Resumen Día 1 - Primera hora */}
                      <td>
                        {vehicle.days[1][REPORT_HOURS[0]]?.time || "-"}
                      </td>
                      <td>
                        {vehicle.days[1][REPORT_HOURS[0]]?.speed > 0 
                          ? `${vehicle.days[1][REPORT_HOURS[0]].speed} km/h` 
                          : "-"}
                      </td>
                      <td className="location-cell">
                        {vehicle.days[1][REPORT_HOURS[0]]?.location || "Sin reporte"}
                      </td>
                      
                      {/* Resumen Día 2 - Primera hora */}
                      <td>
                        {vehicle.days[2][REPORT_HOURS[0]]?.time || "-"}
                      </td>
                      <td>
                        {vehicle.days[2][REPORT_HOURS[0]]?.speed > 0 
                          ? `${vehicle.days[2][REPORT_HOURS[0]].speed} km/h` 
                          : "-"}
                      </td>
                      <td className="location-cell">
                        {vehicle.days[2][REPORT_HOURS[0]]?.location || "Sin reporte"}
                      </td>
                      
                      {/* Resumen Día 3 - Primera hora */}
                      <td>
                        {vehicle.days[3][REPORT_HOURS[0]]?.time || "-"}
                      </td>
                      <td>
                        {vehicle.days[3][REPORT_HOURS[0]]?.speed > 0 
                          ? `${vehicle.days[3][REPORT_HOURS[0]].speed} km/h` 
                          : "-"}
                      </td>
                      <td className="location-cell">
                        {vehicle.days[3][REPORT_HOURS[0]]?.location || "Sin reporte"}
                      </td>
                      
                      {/* Acciones */}
                      <td>
                        <Tooltip title={expandedHours[vehicle.plate] ? "Ocultar horas" : "Ver todas las horas"}>
                          <IconButton 
                            size="small"
                            onClick={() => toggleVehicleHours(vehicle.plate)}
                          >
                            {expandedHours[vehicle.plate] ? 
                              <KeyboardArrowUpIcon fontSize="small" /> : 
                              <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                    
                    {/* Fila con información de proveedor */}
                    <tr className={`GM__${theme}-location-report-provider-row ${expandedHours[vehicle.plate] ? 'visible' : ''}`}>
                      <td colSpan="8" className={`GM__${theme}-location-report-provider`}>
                        <strong>Proveedor:</strong> {vehicle.provider.toUpperCase()}
                      </td>
                      <td colSpan="3" className="observations-cell">
                        <strong>Observaciones:</strong> Sin observaciones
                      </td>
                      <td colSpan="3" className="evidence-cell">
                        <strong>Evidencia GPS 1:</strong> No disponible
                      </td>
                      <td colSpan="3" className="evidence-cell">
                        <strong>Evidencia GPS 2:</strong> No disponible
                      </td>
                      <td></td>
                    </tr>
                    
                    {/* Filas adicionales para cada hora (expandibles) */}
                    {expandedHours[vehicle.plate] && REPORT_HOURS.slice(1).map((hour, hourIndex) => (
                      <tr key={`${vehicle.plate}-${hour}`} className={`GM__${theme}-location-report-hour-row`}>
                        <td colSpan="8" className="hour-label">
                          <strong>Hora {hour}</strong>
                        </td>
                        
                        {/* Día 1 */}
                        <td>
                          {vehicle.days[1][hour]?.time || "-"}
                        </td>
                        <td>
                          {vehicle.days[1][hour]?.speed > 0 
                            ? `${vehicle.days[1][hour].speed} km/h` 
                            : "-"}
                        </td>
                        <td className="location-cell">
                          {vehicle.days[1][hour]?.location || "Sin reporte"}
                        </td>
                        
                        {/* Día 2 */}
                        <td>
                          {vehicle.days[2][hour]?.time || "-"}
                        </td>
                        <td>
                          {vehicle.days[2][hour]?.speed > 0 
                            ? `${vehicle.days[2][hour].speed} km/h` 
                            : "-"}
                        </td>
                        <td className="location-cell">
                          {vehicle.days[2][hour]?.location || "Sin reporte"}
                        </td>
                        
                        {/* Día 3 */}
                        <td>
                          {vehicle.days[3][hour]?.time || "-"}
                        </td>
                        <td>
                          {vehicle.days[3][hour]?.speed > 0 
                            ? `${vehicle.days[3][hour].speed} km/h` 
                            : "-"}
                        </td>
                        <td className="location-cell">
                          {vehicle.days[3][hour]?.location || "Sin reporte"}
                        </td>
                        
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationReportView;